// Dollhouse — a small 3D top-down preview of the apartment in a corner of the
// canvas. Matterport's signature "wow" feature: lets the buyer see the whole
// floor plan as volumes at a glance and tap any room to enter it.
//
// Implementation:
// • Second pc.CameraComponent with `rect` set to a normalised corner viewport
//   (default: top-right 24% × 24%). Renders only the dedicated "dollhouse"
//   layer, so the panorama sphere and hotspots stay out of the picture.
// • One box entity per room, scaled to the room's floor-plan bounding box at
//   2.7 m wall height. Apartment +X → world +X, apartment +Y → world -Z.
// • Slow ambient orbit so the geometry reads as 3D. Click overlay buttons
//   (positioned via worldToScreen each frame) handle enter-room interaction.

import * as pc from 'playcanvas';
import { Emitter } from '../app/emitter';
import type { ApartmentFeature, Room } from '../app/types';

function shortestAngleDelta(current: number, target: number): number {
  let delta = (target - current) % 360;
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;
  return delta;
}

function wrapDeg(deg: number): number {
  let v = deg % 360;
  if (v < 0) v += 360;
  return v;
}

const DOLLHOUSE_LAYER_NAME = 'dollhouse';
const WALL_HEIGHT_M = 2.7;
const VIEWPORT_RECT = new pc.Vec4(0.745, 0.66, 0.24, 0.28); // x, y, w, h (normalised, y from bottom)
/**
 * How quickly the dollhouse pivot chases the camera yaw. 1 = instant,
 * 0 = never moves. Higher feels responsive but causes visible jitter on
 * fast drag-looks. 6/sec lands a smooth 100ms time constant.
 */
const YAW_FOLLOW_RATE_PER_SEC = 6;
/**
 * Slow ambient drift applied when the user isn't moving the camera, so the
 * dollhouse always reads as 3D rather than a flat illustration.
 */
const AMBIENT_DRIFT_DEG_PER_SEC = 4;

interface DollhouseRoom {
  room: Room;
  entity: pc.Entity;
  material: pc.StandardMaterial;
  /** Room centre in dollhouse world space, used for HTML overlay positioning. */
  centre: pc.Vec3;
  button: HTMLButtonElement;
}

/**
 * Apartment-level architectural feature (staircase, opening) — rendered as
 * a non-interactive decorative block so the dollhouse reads as architecture,
 * not a row of room cubes. No HTML overlay button; the user can't click it.
 */
interface DollhouseFeature {
  feature: ApartmentFeature;
  entity: pc.Entity;
  material: pc.StandardMaterial;
}

/** Staircase block height — shorter than walls so it reads as "stairs go down here". */
const STAIR_HEIGHT_M = 1.2;
/** Cool slate tint for the staircase block, distinct from any room variant. */
const STAIR_TINT: [number, number, number] = [0.42, 0.46, 0.55];

export interface DollhouseSelectEvent {
  room: Room;
}

const FOV_CONE_LENGTH_M = 3.2;          // how far the wedge extends from the camera in apartment metres
const FOV_CONE_HALF_ANGLE_DEG = 30;     // 60° apex — matches the panorama camera's FOV roughly

/** Sun arrow geometry — small orange wedge floating outside the apartment perimeter. */
const SUN_ARROW_LENGTH_M = 1.4;
const SUN_ARROW_HALF_WIDTH_M = 0.45;
/** Multiplier on the apartment bounding span — places the arrow outside the rooms. */
const SUN_ARROW_RADIUS_FACTOR = 0.85;
const SUN_ARROW_HEIGHT_M = 0.6;         // sits above the wall tops so it always reads

/**
 * Per-variant base tint applied to each room's dollhouse box, so the buyer can
 * scan the apartment and see at a glance which finish package each room is
 * currently on. Values are deliberately desaturated — the dollhouse is a
 * schematic, not a render, so it shouldn't compete with the panorama colour.
 * Unknown variant ids fall back to the neutral slate.
 */
const VARIANT_TINTS: Record<string, [number, number, number]> = {
  before:        [0.32, 0.34, 0.42], // neutral slate (matches the legacy default)
  warm_luxury:   [0.50, 0.36, 0.22], // muted warm bronze
  minimal_white: [0.55, 0.58, 0.62], // bright cool grey
  dark_premium:  [0.20, 0.18, 0.26], // deep charcoal-purple
};
const DEFAULT_VARIANT_TINT: [number, number, number] = [0.32, 0.34, 0.42];
/** Intensity multiplier for boxes the buyer has not yet entered. */
const UNVISITED_TINT_MULTIPLIER = 0.45;
/** Box opacity per visit state — gives a second at-a-glance signal beyond colour. */
const VISITED_OPACITY = 0.92;
const UNVISITED_OPACITY = 0.70;

export class Dollhouse {
  private readonly app: pc.Application;
  private readonly buttonContainer: HTMLElement;
  private readonly emitter = new Emitter<DollhouseSelectEvent>();
  private readonly layer: pc.Layer;
  private readonly cameraEntity: pc.Entity;
  private readonly rooms: DollhouseRoom[] = [];
  private readonly features: DollhouseFeature[] = [];
  private readonly screenScratch = new pc.Vec3();
  /** Rooms the buyer has entered. Used for the visited/unvisited contrast. */
  private readonly visited = new Set<string>();
  /** Most recent variant id per room, fed via setRoomVariant(). */
  private readonly variantByRoom = new Map<string, string>();
  private currentRoomId: string | null = null;
  private apartmentCentre = new pc.Vec3();
  private apartmentSpan = 6;
  /** Current dollhouse camera orbit yaw (degrees). */
  private currentYawDeg = 30;
  /**
   * What yaw the camera is chasing — driven by setUserYaw(). Smoothly
   * approached each tick so dragging-look in the panorama orbits the
   * dollhouse in real time (compass behaviour).
   */
  private targetYawDeg = 30;
  /**
   * Free-running counter added on top of the user-driven target so the
   * dollhouse keeps reading as 3D even when the camera is idle.
   */
  private ambientDriftDeg = 0;
  /**
   * FOV cone — flat gold wedge in the apartment plane that points in the
   * panorama camera's view direction. Re-anchored on every room change.
   */
  private fovEntity: pc.Entity | null = null;
  private fovMaterial: pc.StandardMaterial | null = null;
  /**
   * Sun arrow — small orange wedge floating outside the apartment perimeter,
   * pointing toward the apartment centre from the current solar azimuth.
   * Pure compass indicator: doesn't move with the camera, only when the
   * SunSystem's time-of-day changes. Idea #9 from DOLLHOUSE-IDEAS.md.
   */
  private sunArrowEntity: pc.Entity | null = null;
  private sunArrowMaterial: pc.StandardMaterial | null = null;
  /** Current solar azimuth (0=N, 90=E, 180=S, 270=W). null = below horizon (hidden). */
  private sunAzimuthDeg: number | null = null;
  /** Whether the sun is above the horizon — toggles the arrow's visibility. */
  private sunVisible = false;

  constructor(app: pc.Application, _canvas: HTMLCanvasElement, buttonContainer: HTMLElement) {
    this.app = app;
    this.buttonContainer = buttonContainer;

    const existing = app.scene.layers.getLayerByName(DOLLHOUSE_LAYER_NAME);
    if (existing) {
      this.layer = existing;
    } else {
      this.layer = new pc.Layer({ name: DOLLHOUSE_LAYER_NAME });
      app.scene.layers.push(this.layer);
    }

    // Camera lives directly under app.root — no pivot. Each tick computes
    // the world position from currentYawDeg + apartment centre and calls
    // lookAt directly. Cleaner than a parent/child rotation chain that the
    // engine has to recompute every frame.
    this.cameraEntity = new pc.Entity('dollhouse-cam');
    this.cameraEntity.addComponent('camera', {
      clearColor: new pc.Color(0.04, 0.05, 0.08, 1),
      fov: 36,
      nearClip: 0.5,
      farClip: 100,
      projection: pc.PROJECTION_PERSPECTIVE,
      layers: [this.layer.id],
      priority: 1, // render after the main camera
      rect: VIEWPORT_RECT,
    });
    app.root.addChild(this.cameraEntity);

    // PlayCanvas 2.x fires 'update' on the app and 'postUpdate' (camelCase)
    // only on app.systems — `app.on('postupdate', …)` would silently never
    // fire. Use 'update' so the compass actually ticks each frame.
    this.app.on('update', this.tick);
  }

  setRooms(
    rooms: readonly Room[],
    currentRoomId: string,
    features: readonly ApartmentFeature[] = [],
  ): void {
    this.clear();
    this.currentRoomId = currentRoomId;
    // Reset visit history; the initial room counts as visited so it isn't
    // rendered as a pale "unvisited" box at app start.
    this.visited.clear();
    this.visited.add(currentRoomId);
    for (const room of rooms) {
      if (!room.floorplan) continue;
      this.rooms.push(this.spawnRoom(room));
    }
    for (const feature of features) {
      if (feature.type === 'staircase') {
        this.features.push(this.spawnStaircase(feature));
      }
    }
    this.updateMaterials();
    this.fitCameraToRooms();
    this.spawnFovCone();
  }

  setCurrentRoom(roomId: string): void {
    this.currentRoomId = roomId;
    this.visited.add(roomId);
    this.updateMaterials();
    this.spawnFovCone(); // re-anchor at the new room's cameraXY
  }

  /**
   * Inform the dollhouse which variant a given room is currently rendering.
   * Recolours that room's box with the variant's mood tint so the buyer can
   * scan the apartment and see which finish package each room is set to
   * without entering it. App.ts wires this from the VariantManager event.
   */
  setRoomVariant(roomId: string, variantId: string): void {
    const previous = this.variantByRoom.get(roomId);
    if (previous === variantId) return;
    this.variantByRoom.set(roomId, variantId);
    this.updateMaterials();
  }

  /**
   * Push the current solar azimuth + elevation into the dollhouse. The
   * orange sun arrow rotates to point from the sun direction toward the
   * apartment centre. Elevation <= 0 → arrow hidden (sun below horizon).
   * Pass elevationDeg via the second arg so the arrow can disappear at
   * night; callers without elevation can pass any positive value.
   */
  setSunAzimuth(azimuthDeg: number, elevationDeg = 1): void {
    this.sunAzimuthDeg = azimuthDeg;
    this.sunVisible = elevationDeg > 0;
    if (!this.sunArrowEntity) this.spawnSunArrow();
    this.applySunArrowTransform();
    if (this.sunArrowEntity) this.sunArrowEntity.enabled = this.sunVisible;
  }

  /**
   * Push the live panorama-camera yaw into the dollhouse. The pivot smoothly
   * chases this value each tick so the dollhouse rotates as the user
   * drag-looks — a 3D compass that mirrors where the buyer is facing.
   */
  setUserYaw(yawDeg: number): void {
    this.targetYawDeg = yawDeg;
  }

  /**
   * Show or hide the dollhouse viewport + label overlay. The room boxes stay
   * in the scene either way (cheap), but the second camera stops rendering
   * and the labels are hidden so the corner clears.
   */
  setVisible(visible: boolean): void {
    if (this.cameraEntity.camera) this.cameraEntity.camera.enabled = visible;
    for (const dr of this.rooms) dr.button.style.display = visible ? '' : 'none';
    this.buttonContainer.classList.toggle('is-hidden', !visible);
    if (this.fovEntity) this.fovEntity.enabled = visible;
    if (this.sunArrowEntity) this.sunArrowEntity.enabled = visible && this.sunVisible;
  }

  on(listener: (e: DollhouseSelectEvent) => void): () => void {
    return this.emitter.on(listener);
  }

  destroy(): void {
    this.clear();
    this.fovEntity?.destroy();
    this.fovEntity = null;
    this.fovMaterial?.destroy();
    this.fovMaterial = null;
    this.sunArrowEntity?.destroy();
    this.sunArrowEntity = null;
    this.sunArrowMaterial?.destroy();
    this.sunArrowMaterial = null;
    this.cameraEntity.destroy();
    this.app.off('update', this.tick);
  }

  // ── Internals ─────────────────────────────────────────────────

  private spawnRoom(room: Room): DollhouseRoom {
    const fp = room.floorplan!;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of fp.shape) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
    const w = Math.max(0.5, maxX - minX);
    const d = Math.max(0.5, maxY - minY);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;

    const entity = new pc.Entity(`dollhouse:${room.id}`);
    entity.addComponent('render', { type: 'box', layers: [this.layer.id] });
    entity.setLocalScale(w * 0.92, WALL_HEIGHT_M, d * 0.92); // 8% inter-room gap

    // Apartment +Y maps to world -Z so the dollhouse "north" reads up the page
    // when the camera sits south-east of the apartment looking north-west.
    const centre = new pc.Vec3(cx, WALL_HEIGHT_M / 2, -cy);
    entity.setLocalPosition(centre);

    const material = new pc.StandardMaterial();
    material.useLighting = false;
    material.useFog = false;
    material.useTonemap = false;
    material.useSkybox = false;
    material.diffuse = new pc.Color(0, 0, 0);
    material.emissive = new pc.Color(0.32, 0.34, 0.42);
    material.opacity = 0.92;
    material.blendType = pc.BLEND_NORMAL;
    material.update();
    const meshInstances = entity.render?.meshInstances ?? [];
    for (const mi of meshInstances) mi.material = material;

    this.app.root.addChild(entity);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'dollhouse-room-label';
    button.textContent = room.name;
    button.dataset.roomId = room.id;
    button.addEventListener('click', (e) => {
      e.stopPropagation();
      this.emitter.emit({ room });
    });
    this.buttonContainer.appendChild(button);

    return { room, entity, material, centre, button };
  }

  private clear(): void {
    for (const dr of this.rooms) {
      dr.entity.destroy();
      dr.material.destroy();
      dr.button.remove();
    }
    this.rooms.length = 0;
    for (const df of this.features) {
      df.entity.destroy();
      df.material.destroy();
    }
    this.features.length = 0;
  }

  private spawnStaircase(feature: ApartmentFeature): DollhouseFeature {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of feature.shape) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
    const w = Math.max(0.5, maxX - minX);
    const d = Math.max(0.5, maxY - minY);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;

    const entity = new pc.Entity(`dollhouse-feature:${feature.id}`);
    entity.addComponent('render', { type: 'box', layers: [this.layer.id] });
    // 8% inter-block gap matches the room boxes so the staircase reads as
    // peer architecture, not a contained interior element.
    entity.setLocalScale(w * 0.92, STAIR_HEIGHT_M, d * 0.92);
    // Apartment +Y maps to world -Z, same convention as the room boxes.
    entity.setLocalPosition(cx, STAIR_HEIGHT_M / 2, -cy);

    const material = new pc.StandardMaterial();
    material.useLighting = false;
    material.useFog = false;
    material.useTonemap = false;
    material.useSkybox = false;
    material.diffuse = new pc.Color(0, 0, 0);
    material.emissive = new pc.Color(STAIR_TINT[0], STAIR_TINT[1], STAIR_TINT[2]);
    material.opacity = 0.85;
    material.blendType = pc.BLEND_NORMAL;
    material.update();
    const meshInstances = entity.render?.meshInstances ?? [];
    for (const mi of meshInstances) mi.material = material;

    this.app.root.addChild(entity);
    return { feature, entity, material };
  }

  private updateMaterials(): void {
    for (const dr of this.rooms) {
      const isCurrent = dr.room.id === this.currentRoomId;
      const isVisited = this.visited.has(dr.room.id);

      if (isCurrent) {
        // Always gold — the "you are here" signal is the loudest one on the
        // dollhouse and shouldn't be diluted by variant mood.
        dr.material.emissive = new pc.Color(0.96, 0.77, 0.19);
        dr.material.opacity = VISITED_OPACITY;
      } else {
        const variantId = this.variantByRoom.get(dr.room.id);
        const baseTint = (variantId && VARIANT_TINTS[variantId]) || DEFAULT_VARIANT_TINT;
        const m = isVisited ? 1 : UNVISITED_TINT_MULTIPLIER;
        dr.material.emissive = new pc.Color(baseTint[0] * m, baseTint[1] * m, baseTint[2] * m);
        dr.material.opacity = isVisited ? VISITED_OPACITY : UNVISITED_OPACITY;
      }
      dr.material.update();

      dr.button.classList.toggle('is-current', isCurrent);
      dr.button.classList.toggle('is-visited', isVisited && !isCurrent);
    }
  }

  private fitCameraToRooms(): void {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const dr of this.rooms) {
      const fp = dr.room.floorplan!;
      for (const [x, y] of fp.shape) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
    for (const df of this.features) {
      for (const [x, y] of df.feature.shape) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
    if (!Number.isFinite(minX)) return;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    this.apartmentCentre.set(cx, WALL_HEIGHT_M / 2, -cy);
    this.apartmentSpan = Math.max(maxX - minX, maxY - minY);
    // The tick handler computes the camera's orbit position around this
    // centre every frame — no initial position needed here. Just trigger
    // one tick equivalent so the camera isn't sitting at the origin until
    // the first postupdate fires.
    this.placeCameraAtCurrentYaw();
    // Sun arrow's perimeter radius depends on apartmentSpan; re-anchor now.
    this.applySunArrowTransform();
  }

  private placeCameraAtCurrentYaw(): void {
    const yawRad = (this.currentYawDeg * Math.PI) / 180;
    const dist = this.apartmentSpan * 1.7;
    const horiz = dist * 0.95;
    const x = this.apartmentCentre.x + Math.sin(yawRad) * horiz;
    const z = this.apartmentCentre.z + Math.cos(yawRad) * horiz;
    const y = this.apartmentCentre.y + dist * 0.95;
    this.cameraEntity.setPosition(x, y, z);
    this.cameraEntity.lookAt(this.apartmentCentre);
  }

  private spawnFovCone(): void {
    if (this.fovEntity) {
      this.fovEntity.destroy();
      this.fovEntity = null;
    }
    if (!this.currentRoomId) return;
    const dr = this.rooms.find((r) => r.room.id === this.currentRoomId);
    if (!dr || !dr.room.floorplan) return;

    // Build a flat triangle in the XZ plane. Apex at origin, base extending
    // toward -Z (which after the apartment +Y → world -Z mapping points "up
    // the page" / north). Yaw=0 in the panorama then corresponds to "north"
    // and a positive panorama yaw rotates the cone clockwise from above.
    // Winding: apex → right → left = CCW when viewed from +Y above, so the
    // front face (with +Y normals) is what the dollhouse camera sees from
    // overhead. Cull-none is still set as belt-and-braces.
    const half = Math.tan((FOV_CONE_HALF_ANGLE_DEG * Math.PI) / 180) * FOV_CONE_LENGTH_M;
    const positions: number[] = [
      0, 0, 0,                           // apex (camera position)
      half, 0, -FOV_CONE_LENGTH_M,       // right base
      -half, 0, -FOV_CONE_LENGTH_M,      // left base
    ];
    const normals: number[] = [0, 1, 0, 0, 1, 0, 0, 1, 0];
    const uvs: number[] = [0.5, 1, 1, 0, 0, 0];
    const indices: number[] = [0, 1, 2];

    const mesh = new pc.Mesh(this.app.graphicsDevice);
    mesh.setPositions(positions);
    mesh.setNormals(normals);
    mesh.setUvs(0, uvs);
    mesh.setIndices(indices);
    mesh.update();

    if (!this.fovMaterial) {
      const mat = new pc.StandardMaterial();
      mat.useLighting = false;
      mat.useFog = false;
      mat.useTonemap = false;
      mat.useSkybox = false;
      mat.diffuse = new pc.Color(0, 0, 0);
      mat.emissive = new pc.Color(0.96, 0.77, 0.19); // gold
      mat.opacity = 0.75;
      mat.blendType = pc.BLEND_NORMAL;
      mat.depthTest = false;        // always paint on top — never occluded by room boxes
      mat.depthWrite = false;
      mat.cull = pc.CULLFACE_NONE;  // belt-and-braces in case the winding flips
      mat.update();
      this.fovMaterial = mat;
    }

    const meshInstance = new pc.MeshInstance(mesh, this.fovMaterial);
    const entity = new pc.Entity('dollhouse-fov-cone');
    entity.addComponent('render', { meshInstances: [meshInstance], layers: [this.layer.id] });

    // Anchor at the room's camera position in apartment frame. Apartment +Y
    // maps to world -Z, so cameraXY = (cx, cy) goes to world (cx, y, -cy).
    // Float the wedge slightly above the room-box rooftops (WALL_HEIGHT_M)
    // so it reads as a clear directional indicator over the dollhouse plan
    // rather than being buried inside the translucent volumes.
    const [cx, cy] = dr.room.floorplan.cameraXY;
    entity.setPosition(cx, WALL_HEIGHT_M + 0.1, -cy);

    this.app.root.addChild(entity);
    this.fovEntity = entity;
    this.applyFovRotation();
  }

  private applyFovRotation(): void {
    if (!this.fovEntity || !this.currentRoomId) return;
    const dr = this.rooms.find((r) => r.room.id === this.currentRoomId);
    const yawOffset = dr?.room.floorplan?.yawOffsetDeg ?? 0;
    // PlayCanvas Y rotation positive is CCW from above (right-hand rule).
    // Panorama yaw positive = "look to the right" (clockwise from above in
    // the user's mental model). Negate so the cone's rotation reads as
    // clockwise on the dollhouse plan.
    this.fovEntity.setLocalEulerAngles(0, -(this.targetYawDeg + yawOffset), 0);
  }

  /**
   * Build the sun arrow once. It's a flat orange wedge similar to the FOV
   * cone but with reversed winding so the arrow tip points *toward* the
   * apartment centre (the sun is "out there", shining in). Same dollhouse
   * layer + depthTest-off treatment so it always reads on top of the room
   * boxes regardless of which side of the apartment it's parked on.
   */
  private spawnSunArrow(): void {
    if (this.sunArrowEntity) return;

    const positions: number[] = [
      0, 0, 0,                                        // tip (points at apartment centre)
      -SUN_ARROW_HALF_WIDTH_M, 0, SUN_ARROW_LENGTH_M, // back-left
      SUN_ARROW_HALF_WIDTH_M, 0, SUN_ARROW_LENGTH_M,  // back-right
    ];
    const normals: number[] = [0, 1, 0, 0, 1, 0, 0, 1, 0];
    const uvs: number[] = [0.5, 1, 0, 0, 1, 0];
    const indices: number[] = [0, 1, 2];

    const mesh = new pc.Mesh(this.app.graphicsDevice);
    mesh.setPositions(positions);
    mesh.setNormals(normals);
    mesh.setUvs(0, uvs);
    mesh.setIndices(indices);
    mesh.update();

    if (!this.sunArrowMaterial) {
      const mat = new pc.StandardMaterial();
      mat.useLighting = false;
      mat.useFog = false;
      mat.useTonemap = false;
      mat.useSkybox = false;
      mat.diffuse = new pc.Color(0, 0, 0);
      mat.emissive = new pc.Color(1.0, 0.55, 0.20); // warm sun orange
      mat.opacity = 0.85;
      mat.blendType = pc.BLEND_NORMAL;
      mat.depthTest = false;
      mat.depthWrite = false;
      mat.cull = pc.CULLFACE_NONE;
      mat.update();
      this.sunArrowMaterial = mat;
    }

    const meshInstance = new pc.MeshInstance(mesh, this.sunArrowMaterial);
    const entity = new pc.Entity('dollhouse-sun-arrow');
    entity.addComponent('render', { meshInstances: [meshInstance], layers: [this.layer.id] });
    this.app.root.addChild(entity);
    this.sunArrowEntity = entity;
  }

  /**
   * Place the sun arrow at a point on the perimeter circle at the current
   * solar azimuth, and rotate its tip to point inward toward the apartment
   * centre. Compass convention: azimuth 0 = north (which in the dollhouse
   * frame is apartment +Y → world -Z), positive = clockwise from above.
   */
  private applySunArrowTransform(): void {
    if (!this.sunArrowEntity || this.sunAzimuthDeg === null) return;
    const az = this.sunAzimuthDeg;
    const radius = Math.max(2, this.apartmentSpan * SUN_ARROW_RADIUS_FACTOR);

    // Project the azimuth onto the dollhouse plane. azimuth 0 (north) →
    // apartment +Y → world -Z. azimuth +90 (east) → apartment +X → world +X.
    const azRad = (az * Math.PI) / 180;
    const offsetX = Math.sin(azRad) * radius;
    const offsetZ = -Math.cos(azRad) * radius;
    const cx = this.apartmentCentre.x + offsetX;
    const cz = this.apartmentCentre.z + offsetZ;
    this.sunArrowEntity.setPosition(cx, WALL_HEIGHT_M + SUN_ARROW_HEIGHT_M, cz);

    // Rotate the arrow so its +Z (back) points outward (away from centre) and
    // its tip (origin) points inward. PlayCanvas Y rotation is CCW from
    // above; azimuth is clockwise, so we negate.
    this.sunArrowEntity.setLocalEulerAngles(0, -az, 0);
  }

  private readonly tick = (dt: number): void => {
    if (this.rooms.length === 0) return;

    // Compose the orbit yaw from two sources:
    //   1. Smoothly chase the user's panorama-camera yaw → compass behaviour.
    //   2. Add a slow ambient drift so the dollhouse keeps reading as 3D
    //      when the user is idle (no drag-look).
    this.ambientDriftDeg = (this.ambientDriftDeg + AMBIENT_DRIFT_DEG_PER_SEC * dt) % 360;
    const targetWithDrift = this.targetYawDeg + this.ambientDriftDeg;
    const delta = shortestAngleDelta(this.currentYawDeg, targetWithDrift);
    const t = Math.min(1, YAW_FOLLOW_RATE_PER_SEC * dt);
    this.currentYawDeg = wrapDeg(this.currentYawDeg + delta * t);

    // Direct camera orbit: position computed from yaw + distance, lookAt the
    // apartment centre. No pivot/child transform inheritance.
    this.placeCameraAtCurrentYaw();

    // FOV cone tracks the panorama yaw directly (no smoothing — instant
    // feedback so the user sees the cone rotate as they drag-look).
    this.applyFovRotation();

    // Position room labels via the dollhouse camera's worldToScreen.
    // PC 2.x's CameraComponent.worldToScreen passes the canvas's clientRect
    // (CSS pixels) into Camera.worldToScreen AND folds the camera's rect into
    // the screen-space mapping, so the returned coords are already CSS pixels
    // landing on the visible sub-rect. Earlier code divided by a backing/CSS
    // ratio left over from PC 1.x conventions; on high-DPI displays that
    // shifted the labels toward the upper-left of the canvas instead of
    // anchoring them to the dollhouse boxes.
    const cam = this.cameraEntity.camera;
    if (!cam) return;
    for (const dr of this.rooms) {
      cam.worldToScreen(dr.centre, this.screenScratch);
      const inFront = this.screenScratch.z > 0;
      if (!inFront) {
        dr.button.style.display = 'none';
        continue;
      }
      dr.button.style.display = '';
      dr.button.style.left = `${this.screenScratch.x}px`;
      dr.button.style.top = `${this.screenScratch.y}px`;
    }
  };
}
